import { FEED_JOBS_STATUSES } from "../globals/constants";
import { storeFeedJobId } from "./appStorage";
import { api } from "services";

export const isJobInProgress = job =>
	job.status === FEED_JOBS_STATUSES.PENDING || job.status === FEED_JOBS_STATUSES.PROCESSING;
export const isJobFinishedAndOutdated = job =>
	job.status === FEED_JOBS_STATUSES.FINISHED && (new Date(job.endTimestamp).getTime() + 86400 < new Date().getTime());

/**
 * Wait for the given milliseconds
 * @param {number} milliseconds The given time to wait
 * @returns {Promise} A fulfilled promise after the given time has passed
 */
const waitFor = milliseconds => new Promise(resolve => setTimeout(resolve, milliseconds));

/**
 * Execute a promise and retry with exponential backoff
 * based on the maximum retry attempts it can perform
 */
const retry = (promise, shouldRetry) => {
	const maxRetries = 5;

	const retryWithBackoff = async retries =>  {
		try {
			if (retries > 0) {
				// Here is where the magic happens.
				// on every retry, we exponentially increase the time to wait.
				// Here is how it looks for a `maxRetries` = 5
				// (2 ** 1) * 1000 = 2000 ms
				// (2 ** 2) * 1000 = 4000 ms
				// (2 ** 3) * 1000 = 8000 ms
				const timeToWait = 2 ** retries * 1000;
				await waitFor(timeToWait);
			}

			const response = await promise();

			if (shouldRetry(response)) {
				if (retries < maxRetries) {
					return retryWithBackoff(retries + 1);
				} else {
					return retryWithBackoff(retries);
				}
			}

			return response;
		} catch (e) {
			throw e;
		}
	};

	return retryWithBackoff(0);
};

/**
 * @typedef Payload
 * @type {object}
 * @property {string} submissionType - award || proposal
 * @property {string} fields - radiobuttons16,principal-investigator,single-select17
 * @property {string} filename - fn3
 * @property {string=} sort - awardNumber
 * @property {string=} search - ''
 */

/**
 *
 * @param {Payload} payload - payload body
 * @param args
 * @returns {Promise<unknown>}
 */
export const exportCsv = async (payload, args = {}) => {
	let job = {};

	try {
		const { jobId } = await api.feedJobs.exportCsv(payload, args);
		storeFeedJobId(jobId);

		job = await getJobStatus(jobId);

		return {
			job,
			error: null,
			status: job.status,
			message: job.errorMessage,
		};
	} catch (e) {
		return {
			job,
			error: e,
			status: e.status,
			message: e.message,
		};
	}
};

export const getJobStatus = jobId => {
	return retry(
		() => api.feedJobs.getStatus({ id: jobId }),
		isJobInProgress,
	);
};

export const getCsvBlob = async resultUrl => {
	let csv = {};
	try {
		csv = await fetch(resultUrl);

		return csv.blob();
	} catch (e) {
		return {
			csv,
			error: e,
			status: "ERROR",
			message: e.message,
		};
	}
};

export const downloadCsvFile = job => {
	const href = job.resultUrl;
	const anchor = document.createElement("a");
	document.body.appendChild(anchor);

	anchor.href = href;
	anchor.download = job.payload.filename;
	anchor.click();

	window.URL.revokeObjectURL(href);
};
